import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Skeleton,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CancelIcon from '@mui/icons-material/Cancel';
import { format } from 'date-fns';
import {
  useSubscription,
  useInvoices,
  usePaymentMethod,
  useCreatePortalSession,
  useCancelSubscription,
  getUsagePercentage,
  formatStorage,
  getTierDisplayName,
  getStatusInfo,
} from '../../hooks/useSubscription';
import { PricingPlans } from '../../components/settings/PricingPlans';
import { useAuth } from '../../contexts/AuthContext';

const USAGE_LABELS: Record<string, string> = {
  clients: 'Clients',
  projects: 'Projects',
  tasks: 'Tasks',
  notes: 'Notes',
  templates: 'Templates',
  landingPages: 'Landing Pages',
  invoicesMonthly: 'Invoices (Monthly)',
  contractsMonthly: 'Contracts (Monthly)',
  leadsMonthly: 'Leads (Monthly)',
  aiActionsMonthly: 'AI Actions (Monthly)',
  storageUsedBytes: 'Storage',
};

export default function BillingSettingsPage() {
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const { data: subscriptionData, isLoading: subLoading, refetch } = useSubscription();
  const { data: invoicesData, isLoading: invoicesLoading } = useInvoices(5);
  const { data: paymentMethodData, isLoading: pmLoading } = usePaymentMethod();
  const portalSession = useCreatePortalSession();
  const cancelSubscription = useCancelSubscription();

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const success = searchParams.get('success') === 'true';
  const canceled = searchParams.get('canceled') === 'true';

  // Refetch subscription and user data on success to update tier icon
  useEffect(() => {
    if (success) {
      // Refetch subscription data
      refetch();
      // Also refresh user/organization data to update tier in sidebar/avatar
      refreshUser().catch(console.error);
    }
  }, [success, refetch, refreshUser]);

  const handleManageBilling = async () => {
    try {
      const result = await portalSession.mutateAsync(undefined);
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (error) {
      console.error('Failed to open billing portal:', error);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setCancelError(null);
      await cancelSubscription.mutateAsync();
      setCancelDialogOpen(false);
      // Refresh user data to reflect canceled status
      await refreshUser();
    } catch (error: any) {
      setCancelError(error?.message || 'Failed to cancel subscription. Please try again.');
    }
  };

  const subscription = subscriptionData?.subscription;
  const usage = subscriptionData?.usage;
  const limits = subscriptionData?.limits;
  const paymentMethod = paymentMethodData?.paymentMethod;
  const invoices = invoicesData?.invoices || [];

  const statusInfo = subscription ? getStatusInfo(subscription.status) : null;

  return (
    <Container maxWidth="lg">
      <Box py={4}>
        <Typography variant="h4" fontWeight="bold" mb={1}>
          Billing & Subscription
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={4}>
          Manage your subscription, view usage, and update payment methods.
        </Typography>

        {/* Success/Cancel Alerts */}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Your subscription has been updated successfully!
          </Alert>
        )}
        {canceled && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Checkout was canceled. No changes were made.
          </Alert>
        )}

        <Grid container spacing={4}>
          {/* Current Plan Card */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" mb={2}>
                  Current Plan
                </Typography>

                {subLoading ? (
                  <Box>
                    <Skeleton variant="text" width={120} height={40} />
                    <Skeleton variant="text" width={200} />
                  </Box>
                ) : subscription ? (
                  <Box>
                    <Box display="flex" alignItems="center" gap={2} mb={2}>
                      <Typography variant="h4" fontWeight="bold">
                        {getTierDisplayName(subscription.tier)}
                      </Typography>
                      {statusInfo && (
                        <Chip
                          label={statusInfo.label}
                          color={statusInfo.color}
                          size="small"
                        />
                      )}
                    </Box>

                    {subscription.status === 'trialing' && subscription.trialDaysRemaining > 0 && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        {subscription.trialDaysRemaining} days remaining in your trial
                      </Alert>
                    )}

                    {subscription.billingCycle && (
                      <Typography variant="body2" color="text.secondary" mb={1}>
                        Billing: {subscription.billingCycle === 'annual' ? 'Annual' : 'Monthly'}
                      </Typography>
                    )}

                    {subscription.currentPeriodEnd && (
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        {subscription.cancelAtPeriodEnd
                          ? 'Cancels on: '
                          : 'Renews on: '}
                        {format(new Date(subscription.currentPeriodEnd), 'MMM d, yyyy')}
                      </Typography>
                    )}

                    {subscription.cancelAtPeriodEnd && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        Your subscription will be canceled at the end of the billing period.
                      </Alert>
                    )}

                    <Button
                      variant="outlined"
                      onClick={handleManageBilling}
                      disabled={portalSession.isPending || subscription.status === 'trialing'}
                    >
                      Manage Billing
                    </Button>
                  </Box>
                ) : (
                  <Typography color="text.secondary">
                    No subscription found
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Payment Method Card */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <CreditCardIcon />
                  <Typography variant="h6" fontWeight="bold">
                    Payment Method
                  </Typography>
                </Box>

                {pmLoading ? (
                  <Skeleton variant="text" width={200} />
                ) : paymentMethod?.card ? (
                  <Box>
                    <Typography variant="body1" mb={1}>
                      {paymentMethod.card.brand.toUpperCase()} ending in{' '}
                      {paymentMethod.card.last4}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      Expires {paymentMethod.card.expMonth}/{paymentMethod.card.expYear}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleManageBilling}
                      disabled={portalSession.isPending}
                    >
                      Update
                    </Button>
                  </Box>
                ) : (
                  <Typography color="text.secondary">
                    No payment method on file
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Usage Stats */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" mb={3}>
                  Usage This Period
                </Typography>

                {subLoading ? (
                  <Grid container spacing={2}>
                    {[1, 2, 3, 4].map((i) => (
                      <Grid item xs={12} sm={6} md={3} key={i}>
                        <Skeleton variant="text" width={100} />
                        <Skeleton variant="rectangular" height={8} />
                      </Grid>
                    ))}
                  </Grid>
                ) : usage && limits ? (
                  <Grid container spacing={3}>
                    {Object.entries(USAGE_LABELS).map(([key, label]) => {
                      const usageKey = key as keyof typeof usage;
                      const usageValue = usage[usageKey];
                      const percentage = getUsagePercentage(usage, limits, usageKey);
                      const limitKey = key === 'storageUsedBytes' ? 'storageGb' : key;
                      const limitValue = (limits as any)[limitKey];
                      const isUnlimited = limitValue === -1;

                      return (
                        <Grid item xs={12} sm={6} md={4} key={key}>
                          <Box mb={1}>
                            <Box display="flex" justifyContent="space-between" mb={0.5}>
                              <Typography variant="body2" fontWeight="medium">
                                {label}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {key === 'storageUsedBytes'
                                  ? `${formatStorage(usageValue)} / ${
                                      isUnlimited ? 'Unlimited' : `${limitValue} GB`
                                    }`
                                  : `${usageValue} / ${
                                      isUnlimited ? 'Unlimited' : limitValue
                                    }`}
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={percentage}
                              sx={{
                                height: 8,
                                borderRadius: 4,
                                bgcolor: 'grey.200',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor:
                                    percentage >= 90
                                      ? 'error.main'
                                      : percentage >= 75
                                      ? 'warning.main'
                                      : 'primary.main',
                                },
                              }}
                            />
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                ) : null}
              </CardContent>
            </Card>
          </Grid>

          {/* Invoice History */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={3}>
                  <ReceiptIcon />
                  <Typography variant="h6" fontWeight="bold">
                    Invoice History
                  </Typography>
                </Box>

                {invoicesLoading ? (
                  <Skeleton variant="rectangular" height={200} />
                ) : invoices.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Invoice</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Amount</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invoices.map((invoice) => (
                          <TableRow key={invoice.id}>
                            <TableCell>{invoice.number || invoice.id}</TableCell>
                            <TableCell>
                              {format(new Date(invoice.created), 'MMM d, yyyy')}
                            </TableCell>
                            <TableCell>
                              ${invoice.amount.toFixed(2)} {invoice.currency.toUpperCase()}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={invoice.status || 'Unknown'}
                                size="small"
                                color={
                                  invoice.status === 'paid'
                                    ? 'success'
                                    : invoice.status === 'open'
                                    ? 'warning'
                                    : 'default'
                                }
                              />
                            </TableCell>
                            <TableCell align="right">
                              {invoice.pdfUrl && (
                                <Button
                                  size="small"
                                  href={invoice.pdfUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Download
                                </Button>
                              )}
                              {invoice.hostedUrl && (
                                <Button
                                  size="small"
                                  href={invoice.hostedUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  View
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography color="text.secondary">
                    No invoices yet
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Pricing Plans */}
          <Grid item xs={12}>
            <Divider sx={{ my: 4 }} />
            <Typography variant="h5" fontWeight="bold" textAlign="center" mb={1}>
              Upgrade Your Plan
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              textAlign="center"
              mb={4}
            >
              Choose the plan that's right for your business
            </Typography>
            <PricingPlans />
          </Grid>

          {/* Cancel Subscription Section */}
          {subscription && subscription.status !== 'expired' && subscription.status !== 'canceled' && (
            <Grid item xs={12}>
              <Divider sx={{ my: 4 }} />
              <Card sx={{ borderColor: 'error.main', borderWidth: 1, borderStyle: 'solid' }}>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <CancelIcon color="error" />
                    <Typography variant="h6" fontWeight="bold" color="error">
                      Cancel Subscription
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    If you cancel your subscription, your account will be deactivated immediately.
                    You will lose access to all features and your data. This action cannot be undone.
                    If you wish to use Elevair again in the future, you will need to create a new account
                    with a different email address.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setCancelDialogOpen(true)}
                  >
                    Cancel Subscription
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>

        {/* Cancel Subscription Confirmation Dialog */}
        <Dialog
          open={cancelDialogOpen}
          onClose={() => setCancelDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ color: 'error.main' }}>
            Cancel Subscription?
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to cancel your subscription? This action will:
            </DialogContentText>
            <Box component="ul" sx={{ mt: 2, pl: 2 }}>
              <li>
                <Typography variant="body2">
                  Immediately deactivate your account
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Stop all recurring payments
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Remove access to all features
                </Typography>
              </li>
              <li>
                <Typography variant="body2" fontWeight="bold">
                  You will not be able to rejoin with the same email address
                </Typography>
              </li>
            </Box>
            {cancelError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {cancelError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2.5 }}>
            <Button
              onClick={() => setCancelDialogOpen(false)}
              disabled={cancelSubscription.isPending}
            >
              Keep Subscription
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleCancelSubscription}
              disabled={cancelSubscription.isPending}
            >
              {cancelSubscription.isPending ? 'Canceling...' : 'Yes, Cancel Subscription'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
}
